import CloseIcon from "@mui/icons-material/Close";
import { ReactComponent as TranslateIcon } from "../../../../assets/translate.svg";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Modal,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useLanguages } from "../../../../context/LanguagesContext";
import { useToast } from "../../../../context/ToastContext";
import { translateTitle } from "../../../../services/api/apiClient";
import { brand, gray } from "../../../../theme/themePrimitives";
import { extractErrorMessage } from "../../../../utils/errorUtils";
import { MAX_LENGTH_DESCRIPTION } from "../../../../utils/inputLimits";

interface TranslateFieldModalProps {
  open: boolean;
  onClose: () => void;
  fieldLabel: string;
  englishValue: string;
  onEnglishValueChange: (value: string) => void;
  translations: Record<string, string>;
  onSave: (translations: Record<string, string>) => void;
}

export const TranslateFieldModal: React.FC<TranslateFieldModalProps> = ({
  open,
  onClose,
  fieldLabel,
  englishValue,
  onEnglishValueChange,
  translations,
  onSave,
}) => {
  const { languages, getLanguageDetails } = useLanguages();
  const { showToast } = useToast();
  const [editedValues, setEditedValues] = useState<Record<string, string>>({});
  const [isTranslating, setIsTranslating] = useState(false);
  const isEditMode = Object.values(translations).some((value) => value?.trim());

  const otherLanguageCodes = [...languages.portalDesignLanguages]
    .filter((lang) => lang.langugageCode.toLowerCase() !== "en")
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((lang) => lang.langugageCode);

  useEffect(() => {
    if (open) {
      setEditedValues({ ...translations });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleInputChange = (langCode: string, value: string) => {
    setEditedValues((prev) => ({ ...prev, [langCode]: value }));
  };

  const emptyLanguageCodes = otherLanguageCodes.filter(
    (langCode) => !editedValues[langCode]?.trim(),
  );

  const handleAutoTranslateAll = async () => {
    if (isTranslating || emptyLanguageCodes.length === 0) return;

    setIsTranslating(true);
    try {
      const results = await translateTitle({
        sourceLanguage: "en",
        text: englishValue,
        targetLanguage: emptyLanguageCodes,
      });
      setEditedValues((prev) => {
        const updated = { ...prev };
        results.forEach(({ languageCode, text }) => {
          updated[languageCode] = text;
        });
        return updated;
      });
    } catch (err) {
      showToast(extractErrorMessage(err, "Failed to auto-translate"), "error");
    } finally {
      setIsTranslating(false);
    }
  };

  const handleSave = () => {
    onSave(editedValues);
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          maxHeight: "90vh",
          overflowY: "auto",
          backgroundColor: "white",
          borderRadius: "8px",
          boxShadow: "0px 3px 6px rgba(0, 0, 0, 0.24)",
          padding: "16px",
          width: "50%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h5">{fieldLabel}</Typography>
          <IconButton sx={{ border: "none" }} onClick={onClose}>
            <CloseIcon sx={{ color: "#98A2B3" }} />
          </IconButton>
        </Box>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            paddingBottom: "1rem",
            borderBottom: `1px solid ${gray[300]}`,
          }}
        >
          {isEditMode
            ? "Edit the translations for this title, or auto-translate from English."
            : "Translate this title into the portal's languages, or auto-translate from English."}
        </Typography>
        <Stack spacing={2} sx={{ marginTop: "1rem" }}>
          <Stack spacing={0.5}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="subtitle2">English</Typography>
                <Chip
                  size="small"
                  label="Base"
                  sx={{
                    bgcolor: brand[50],
                    "& .MuiChip-label": { color: brand[700] },
                  }}
                />
              </Box>
              <Tooltip
                title="Fills in empty languages with a translation of the English title. Languages you've already filled in are left as-is."
                arrow
                placement="left"
                slotProps={{
                  tooltip: { sx: { backgroundColor: gray[800] } },
                  arrow: { sx: { color: gray[800] } },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    gap: 0.5,
                    alignItems: "center",
                    cursor:
                      isTranslating ||
                        englishValue.trim() === "" ||
                        emptyLanguageCodes.length === 0
                        ? "default"
                        : "pointer",
                    opacity:
                      isTranslating ||
                        englishValue.trim() === "" ||
                        emptyLanguageCodes.length === 0
                        ? 0.5
                        : 1,
                  }}
                  onClick={
                    englishValue.trim() === "" || emptyLanguageCodes.length === 0
                      ? undefined
                      : handleAutoTranslateAll
                  }
                >
                  {isTranslating ? (
                    <CircularProgress size={18} sx={{ color: brand[600] }} />
                  ) : (
                    <TranslateIcon
                      style={{ color: brand[600], width: 18, height: 18 }}
                    />
                  )}
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    color={brand[600]}
                  >
                    Auto-translate all
                  </Typography>
                </Box>
              </Tooltip>
            </Box>
            <TextField
              fullWidth
              type="text"
              value={englishValue}
              onChange={(e) => onEnglishValueChange(e.target.value)}
              slotProps={{
                htmlInput: { maxLength: MAX_LENGTH_DESCRIPTION },
              }}
            />
          </Stack>
          {otherLanguageCodes.map((langCode) => (
            <Stack spacing={0.5} key={langCode}>
              <Typography variant="subtitle2">
                {getLanguageDetails(langCode)?.name ?? langCode}
              </Typography>
              <TextField
                fullWidth
                type="text"
                placeholder="Enter a translation"
                value={editedValues[langCode] || ""}
                onChange={(e) => handleInputChange(langCode, e.target.value)}
                slotProps={{
                  htmlInput: { maxLength: MAX_LENGTH_DESCRIPTION },
                }}
              />
            </Stack>
          ))}
          <Divider />
          <Button
            onClick={handleSave}
            fullWidth
            sx={{
              borderRadius: "12px",
              backgroundColor: `${brand[600]} !important`,
              color: `white`,
              "&:hover": {
                backgroundColor: `${brand[700]} !important`,
              },
            }}
          >
            <Typography>
              {isEditMode ? "Update" : "Apply translations"}
            </Typography>
          </Button>
        </Stack>
      </Box>
    </Modal>
  );
};
